import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { selectedBranchIdAtom } from '@/store/atoms';

interface DefaultPagination {
  page: number;
  size: number;
}

interface Options {
  defaultSize?: number;
  useQueryParam?: boolean;
}

const DEFAULT_SIZE = 10;
const DEFAULT_PAGE = 1;

const usePagination = <AdditionalParams extends Record<string, unknown>>(
  keys: (keyof AdditionalParams)[] = [], // 주소창 queryString에 사용할 키 배열
  { useQueryParam = true, defaultSize = DEFAULT_SIZE }: Options = {
    useQueryParam: true,
    defaultSize: DEFAULT_SIZE,
  }
) => {
  const selectedBranchId = useAtomValue(selectedBranchIdAtom);
  const prevSelectedBranchId = useRef(selectedBranchId);
  const navigate = useNavigate();
  const { search } = useLocation();
  const getPagination = () => {
    if (useQueryParam) {
      // queryParam 방식 사용: 이 함수를 호출할 때마다 주소창의 querystring을 기반으로 새로운 상태 반환
      const params = new URLSearchParams(search);

      return [
        ['page', params.get('page') ?? DEFAULT_PAGE],
        ['size', params.get('size') ?? defaultSize],
        ...keys.map<[string, string | undefined]>((key) => [
          key as string,
          params.get(key as string) ?? undefined,
        ]),
      ].reduce(
        (acc, [key, value]) => {
          return {
            ...acc,
            [key]:
              value === 'true'
                ? true
                : value === 'false'
                  ? false
                  : !Number.isNaN(Number(value))
                    ? Number(value)
                    : value,
          };
        },
        {} as Partial<AdditionalParams> & DefaultPagination
      );
    }

    // state 방식 사용: 최초 1번만 호출하므로 초깃값만 세팅
    return {
      page: DEFAULT_PAGE,
      size: defaultSize,
    } as Partial<AdditionalParams> & DefaultPagination;
  };

  const [pagination, setPagination] = useState(getPagination());

  useEffect(() => {
    if (
      selectedBranchId !== null &&
      prevSelectedBranchId.current !== null &&
      selectedBranchId !== prevSelectedBranchId.current
    ) {
      reset();
    }
    prevSelectedBranchId.current = selectedBranchId;
  }, [selectedBranchId]);

  useEffect(() => {
    if (useQueryParam) {
      setPagination(getPagination());
    }
  }, [search]);

  const reset = () => {
    if (useQueryParam) {
      navigate({ search: '' });
    } else {
      setPagination(getPagination());
    }
  };

  const onPartialChange = <K extends keyof AdditionalParams>(
    key: K,
    value: AdditionalParams[K]
  ) => {
    onChange({
      [key]: value,
    } as unknown as Partial<AdditionalParams>);
  };

  const onChange = (newValues: Partial<AdditionalParams>) => {
    const params = new URLSearchParams();
    const newParams: Record<string, unknown> = {};

    Object.entries(pagination).forEach(([key, value]) => {
      // pagination 상태값이 빈값이 아니라면 params에 세팅
      if (value !== null && value !== undefined && value !== '') {
        if (useQueryParam) {
          params.set(key, String(value));
        } else {
          newParams[key] = value;
        }
      }
    });

    Object.entries(newValues).forEach(([key, value]) => {
      // 새로 세팅하려는 값이 빈값이라면 params에서 제거
      if (value === null || value === undefined || value === '') {
        if (useQueryParam) {
          params.delete(key);
        } else {
          newParams[key] = undefined;
        }
      } else {
        if (useQueryParam) {
          params.set(key, String(value));
        } else {
          newParams[key] = value;
        }
      }
    });

    // page, size를 제외한 값이 변경되었거나 size에 변화가 생겼을 때 page 초기화
    if (
      Object.keys(newValues).filter(
        (item) => item !== 'page' && item !== 'size'
      ).length > 0 ||
      newValues.size !== pagination.size
    ) {
      if (useQueryParam) {
        params.set('page', String(DEFAULT_PAGE));
      } else {
        newParams.page = DEFAULT_PAGE;
      }
    }

    // params 일괄 적용
    if (useQueryParam) {
      navigate({ search: params.toString() });
    } else {
      setPagination(newParams as Partial<AdditionalParams> & DefaultPagination);
    }
  };

  return {
    query: {
      ...pagination,
    },
    reset,
    setPaginationValue: onPartialChange,
    setPaginationValues: onChange,
  };
};

export default usePagination;
